import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import type { UserStore } from '@/features/iam/users/UserStore'

export class PasswordResetViewModel {
  /**
   * Reset password request id.
   */
  @observable
  accessor resetPasswordRequestId = ''

  /**
   * Email of the user who requested the password reset.
   */
  @observable
  accessor email = ''

  /**
   * Password to be set for the user.
   */
  @observable
  accessor password = ''

  constructor(
    private readonly _userStore: UserStore,
    private readonly _navigate: NavigateFn,
  ) {}

  /**
   * Password contains a lower case letter.
   */
  @computed
  get containsLowerCase() {
    return this.password.toUpperCase() !== this.password
  }

  /**
   * Password contains an upper case letter.
   */
  @computed
  get containsUpperCase() {
    return this.password.toLowerCase() !== this.password
  }

  /**
   * Password contains a number.
   */
  @computed
  get containsOneNumber() {
    return /\d/g.test(this.password)
  }

  /**
   * Password contains special characters.
   */
  @computed
  get containsSpecialCharacters() {
    return /[~`!@#$%^&*+=\\(\\)\-_\\.[\]\\';,/{}|\\":<>\\?]/g.test(
      this.password,
    )
  }

  /**
   * Password minimum length allowed.
   */
  @computed
  get validMinimumLength() {
    return this.password.length >= 8
  }

  /**
   * Password maximum length allowed.
   */
  @computed
  get validMaximumLength() {
    return this.password.length <= 50
  }

  /**
   * Password is valid.
   */
  @computed
  get valid() {
    return (
      this.containsLowerCase &&
      this.containsUpperCase &&
      this.containsOneNumber &&
      this.containsSpecialCharacters &&
      this.validMinimumLength &&
      this.validMaximumLength
    )
  }

  /**
   * Sets the password reset request to complete.
   */
  @action.bound
  setPasswordResetRequest(resetPasswordRequestId: string, email: string) {
    this.resetPasswordRequestId = resetPasswordRequestId
    this.email = email
  }

  /**
   * Resets the password; the view shows a failure from the task's error.
   */
  @action.bound
  async submit() {
    await this.resetPassword().catch(() => undefined)
  }

  /**
   * Resets the password.
   */
  resetPassword = task.resolved(async () => {
    if (!this.valid) {
      throw new Error('Invalid password')
    }

    return this._userStore
      .resetPassword(this.resetPasswordRequestId, this.password)
      .then(this._goToHome)
  })

  /**
   * Redirects to home page.
   */
  @action.bound
  private _goToHome() {
    this._navigate(links.welcome(), { replace: true })
  }
}
